/* eslint-disable react-hooks/set-state-in-effect -- appraisal step effects synchronize external vehicle data and workflow state. */
import React, { useEffect, useCallback, useRef, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button } from 'reactstrap';
import classNames from 'classnames';
import { get } from 'lodash-es';
import {
  CONDITION_LIST,
  CONDITIONS,
  APPRAISAL_PAGE_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { getDefaultMileage, getMileageRangeMessage } from 'site-modules/shared/utils/appraisal/common-utils';
import { FieldError } from 'client/site-modules/shared/components/field-error/field-error';
import { MILEAGE_INPUT, CONDITION_INPUT } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { Select } from 'site-modules/shared/components/select/select';
import { Link } from 'site-modules/shared/components/link/link';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { MILEAGE_FIELD_MASK } from 'site-modules/shared/constants/form-field-masks';
import { useTradeInAppraiserContext } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-context';

/* CSS */
import './condition-and-mileage-step.scss';

/**
 * Renders mileage and condition step.
 * @param {String} mileage
 * @param {String} condition
 * @param {String} year
 * @param {Boolean} isMileageValid
 * @param {Boolean} isStepActive
 * @param {Object} errorMap
 * @param {String} transmissionType
 * @param {Object} engine
 * @param {Boolean} isTransmissionTypeTranslationOverride
 * @param {Boolean} isEngineSizeTranslationOverride
 * @returns {ReactElement}
 */
export function ConditionAndMileageStep({
  mileage: mileageFromProps = '',
  condition = '',
  isMileageValid,
  errorMap = new Map(),
  isStepActive = false,
  transmissionType = null,
  engine = {},
  isTransmissionTypeTranslationOverride = false,
  isEngineSizeTranslationOverride = false,
  year = '',
  isExtendedReport = false,
}) {
  const {
    changeMileage: onMileageChange,
    selectCondition: onConditionChange,
    getMileageRef,
    onMileageBlur,
  } = useTradeInAppraiserContext();
  const [mileage, setMileage] = useState(mileageFromProps);
  const mileageField = useRef(null);
  const debouncedOnMileageChange = useDebounce(onMileageChange, 250);

  useEffect(() => {
    setMileage(mileageFromProps);
  }, [mileageFromProps]);

  const setDefaultMileage = useCallback(() => {
    if (isStepActive && !mileageFromProps && isMileageValid) {
      const defaultMileage = getDefaultMileage(year).toString();
      setMileage(defaultMileage);
      onMileageChange(defaultMileage);
      if (mileageField.current) {
        mileageField.current.touch();
      }
    }
  }, [isStepActive, mileageFromProps, isMileageValid, year, onMileageChange]);

  useEffect(() => {
    if (isStepActive) {
      if (isTransmissionTypeTranslationOverride) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
          event_data: {
            action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
            subaction_name: TrackingConstant.SUBACTION_STYLE_TRANSMISSION_OVERRIDE,
            creative_id: APPRAISAL_PAGE_CREATIVE_ID,
            value: transmissionType,
          },
        });
      }

      if (isEngineSizeTranslationOverride) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
          event_data: {
            action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
            action_category: TrackingConstant.USER_ACTION_CATEGORY,
            action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
            subaction_name: TrackingConstant.SUBACTION_STYLE_ENGINE_OVERRIDE,
            creative_id: APPRAISAL_PAGE_CREATIVE_ID,
            value: get(engine, 'size', null),
          },
        });
      }
    }
  }, [engine, isEngineSizeTranslationOverride, isStepActive, isTransmissionTypeTranslationOverride, transmissionType]);

  useEffect(() => {
    if (isStepActive) {
      setDefaultMileage();
    }
  }, [isStepActive, setDefaultMileage]);

  useEffect(() => {
    if (isStepActive && !condition) {
      onConditionChange({ target: { value: CONDITIONS.AVERAGE } });
    }
  }, [isStepActive, condition, onConditionChange]);

  const errorMileage = errorMap.get(MILEAGE_INPUT);
  const errorCondition = errorMap.get(CONDITION_INPUT);
  const uniqueId = useId();
  const mileageErrorId = `${MILEAGE_INPUT}-error-${uniqueId}`;
  const mileageRangeMessageId = `mileage-range-msg-${uniqueId}`;
  const conditionErrorId = `${CONDITION_INPUT}-error-${uniqueId}`;
  const conditionCommentId = `condition-comment-${uniqueId}`;

  const handleConditionChange = useCallback(
    ({ value }) => {
      onConditionChange({ target: { value } });
    },
    [onConditionChange]
  );

  const handleGotItLinkClick = useCallback(() => {
    onConditionChange({ target: { value: CONDITIONS.AVERAGE } });
  }, [onConditionChange]);

  const getMileageFieldRef = useCallback(
    ref => {
      getMileageRef(ref);
      mileageField.current = ref;
    },
    [getMileageRef]
  );

  const onComponentMileageChange = useCallback(
    value => {
      setMileage(value);
      debouncedOnMileageChange(value);
    },
    [debouncedOnMileageChange]
  );
  const CategoryTitle = isExtendedReport ? 'h3' : 'h4';

  if (!isStepActive) return null;

  const isMileageInputValid = !errorMileage && isMileageValid;

  return (
    <Row className="text-gray-darker condition-and-mileage-step">
      <Col xs={12} md={6}>
        <div className={classNames('pos-r condition-dropdown custom-select-arrow', { 'has-danger': !!errorCondition })}>
          <Select
            name="vehicleCondition"
            className="select form-control form-control-lg text-gray-darker size-16 pe-2 condition-select"
            options={CONDITION_LIST.map(cond => ({ label: cond.title, value: cond.title }))}
            value={condition}
            onChange={handleConditionChange}
            {...(errorCondition ? { ariaDescribedBy: conditionErrorId, ariaInvalid: true } : {})}
            ariaDescribedByAdditional={conditionCommentId}
            labelText={<CategoryTitle className="size-16 mb-0 d-inline">Vehicle Condition</CategoryTitle>}
            labelClassName="mb-1 fw-bold text-gray-darker"
            aria-required
            required
          />
        </div>
        <FieldError id={conditionErrorId} error={errorCondition} classes="mt-1" isLiveRegion />
        <div id={conditionCommentId} className="mt-0_5 size-14 text-gray-dark mb-2_5">
          {condition && CONDITION_LIST.find(cond => cond.title === condition).text}
          <div>
            {condition === CONDITIONS.DAMAGED && (
              <div className="mt-1">
                Sorry,
                <Link to="/" title="Edmunds.com" className="mx-0_25">
                  Edmunds.com
                </Link>
                does not provide estimates for damaged vehicles. To derive a value for a &quot;Damaged&quot; vehicle,
                simply take the &quot;Average&quot; condition and subtract the cost of restoring the vehicle to
                &quot;Average&quot; condition.
                <Button
                  color="link"
                  onClick={handleGotItLinkClick}
                  className="text-capitalize text-underline text-gray-darker p-0 border-0 align-top ms-0_25"
                >
                  <span className="size-14 fw-normal">Got It</span>
                </Button>
              </div>
            )}
          </div>
        </div>
        <FormFieldMask
          id="mileage"
          name="mileage"
          mask={MILEAGE_FIELD_MASK}
          label={<CategoryTitle className="size-16 mb-0 d-inline">Mileage</CategoryTitle>}
          inputClassName="heading-5 fw-normal mileage-field"
          labelClassName="mb-1 fw-bold text-gray-darker"
          ref={getMileageFieldRef}
          defaultValue={mileage}
          isValid={isMileageInputValid}
          ariaInvalid={!isMileageInputValid}
          onChange={onComponentMileageChange}
          onBlur={onMileageBlur}
          {...(errorMileage ? { ariaDescribedBy: mileageErrorId } : {})}
          ariaDescribedByAdditional={mileageRangeMessageId}
          aria-required
          required
          isLiveRegion
        />
        <FieldError id={mileageErrorId} error={errorMileage} isLiveRegion />
        <div id={mileageRangeMessageId} className="mt-0_5 size-14 text-gray-dark">
          {getMileageRangeMessage(year)}
        </div>
      </Col>
    </Row>
  );
}

ConditionAndMileageStep.propTypes = {
  isMileageValid: PropTypes.bool.isRequired,
  isStepActive: PropTypes.bool,
  mileage: PropTypes.string,
  condition: PropTypes.string,
  errorMap: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func])),
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  engine: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
    cylinders: PropTypes.number,
  }),
  transmissionType: PropTypes.string,
  isTransmissionTypeTranslationOverride: PropTypes.bool,
  isEngineSizeTranslationOverride: PropTypes.bool,
  isExtendedReport: PropTypes.bool,
};
